import { z } from "zod"

import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import type {
  Notebook,
  NotebookCell,
  NotebookDocument,
  NotebookSnapshot,
  NotebookSummary,
} from "./notebookSnapshot"

/**
 * Browser client for the Sofia notebooks proxy. Every call names the route
 * organization and active workspace (the same routing fields chat turns send);
 * the proxy exchanges them for a signed context token, so the browser asks for
 * a scope but never asserts one.
 */
const NOTEBOOKS_ENDPOINT = "/api/sofia/notebooks"

type NotebookRequestInit = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>
}

async function fetchNotebooksProxy(
  organizationSlug: string,
  input: string,
  init: NotebookRequestInit = {},
): Promise<Response> {
  const organizationHeaders =
    await getOrganizationBearerHeaders(organizationSlug)
  return fetch(input, {
    ...init,
    headers: { ...init.headers, ...organizationHeaders },
  })
}

function isSafeNotebookActionHref(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//")
}

const notebookSummarySchema: z.ZodType<NotebookSummary> = z.object({
  id: z.string().min(1),
  title: z.string(),
  workspace_id: z.string().min(1),
  created_at: z.string(),
  updated_at: z.string(),
  cell_count: z.number().int().nonnegative(),
  created_by: z.string().nullable(),
  audience: z
    .enum(["private", "workspace", "organization", "public"])
    .optional(),
})

const notebookCellSchema: z.ZodType<NotebookCell> = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("prompt"), text: z.string() }),
    z.object({
      kind: z.literal("research"),
      steps: z.array(
        z.object({
          name: z.string(),
          status: z.string(),
          is_error: z.boolean(),
        }),
      ),
    }),
    z.object({
      kind: z.literal("table"),
      title: z.string().nullable(),
      columns: z.array(z.string()),
      rows: z.array(z.record(z.string(), z.unknown())),
      source: z.enum(["chart", "narrative"]),
    }),
    z.object({
      kind: z.literal("chart"),
      title: z.string(),
      spec: z.record(z.string(), z.unknown()),
    }),
    z.object({ kind: z.literal("text"), text: z.string() }),
    z.object({
      kind: z.literal("action"),
      action_kind: z.string(),
      status: z.string(),
      detail: z.string().optional(),
      href: z
        .string()
        .refine(isSafeNotebookActionHref, "Action links must be in-app routes")
        .optional(),
    }),
  ],
)

const notebookDocumentSchema: z.ZodType<NotebookDocument> = z
  .object({
    version: z.literal(1),
    title: z.string(),
    summary: z.string(),
    sections: z.array(
      z
        .object({
          heading: z.string(),
          body: z.string(),
          embed_cells: z.array(z.number().int().nonnegative()),
        })
        .passthrough(),
    ),
    caveats: z.array(z.string()),
    excluded_cells: z.array(z.number().int().nonnegative()).optional(),
  })
  .passthrough()

export const notebookSchema: z.ZodType<Notebook> = notebookSummarySchema.and(
  z.object({
    snapshot: z.object({
      version: z.literal(1),
      cells: z.array(notebookCellSchema),
    }),
    document: notebookDocumentSchema.nullable().optional(),
    editing_enabled: z.boolean().optional(),
    schedule_error: z.string().optional(),
    locked: z.boolean().optional(),
    revision_id: z.string().nullable().optional(),
    assets: z.record(z.string(), z.string()).optional(),
    chat: z
      .array(
        z.object({ role: z.enum(["user", "assistant"]), content: z.string() }),
      )
      .optional(),
  }),
)

async function parsePayload<T>(
  response: Response,
  schema: z.ZodType<T>,
): Promise<T> {
  const payload: unknown = await response.json().catch(() => null)
  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    throw new Error("Sofia returned invalid notebook data.")
  }
  return parsed.data
}

async function readError(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const data = (await response.json()) as { error?: unknown }
    if (typeof data.error === "string" && data.error) {
      return data.error
    }
  } catch {
    // Non-JSON body — keep the fallback.
  }
  return `${fallback} (${response.status}).`
}

export async function listNotebooks(
  organizationSlug: string,
  workspaceId: string,
  signal?: AbortSignal,
): Promise<NotebookSummary[]> {
  const response = await fetchNotebooksProxy(
    organizationSlug,
    `${NOTEBOOKS_ENDPOINT}?organization=${encodeURIComponent(organizationSlug)}&workspace=${encodeURIComponent(workspaceId)}`,
    { signal, cache: "no-store" },
  )
  if (!response.ok) {
    throw new Error(await readError(response, "Could not load notebooks"))
  }
  const data = await parsePayload(
    response,
    z.object({ notebooks: z.array(notebookSummarySchema) }),
  )
  return data.notebooks
}

export async function getNotebook(
  organizationSlug: string,
  workspaceId: string,
  notebookId: string,
  signal?: AbortSignal,
): Promise<Notebook> {
  const response = await fetchNotebooksProxy(
    organizationSlug,
    `${NOTEBOOKS_ENDPOINT}/${encodeURIComponent(notebookId)}?organization=${encodeURIComponent(organizationSlug)}&workspace=${encodeURIComponent(workspaceId)}`,
    { signal, cache: "no-store" },
  )
  if (!response.ok) {
    throw new Error(await readError(response, "Could not load the notebook"))
  }
  const data = await parsePayload(
    response,
    z.object({ notebook: notebookSchema }),
  )
  return data.notebook
}

export async function createNotebook(args: {
  organizationSlug: string
  workspaceId: string
  title: string
  mode?: "blank" | "chat"
  repeat?: {
    cadence: "daily" | "weekly" | "monthly"
    interval: number
    time: string
    timezone: string
  }
  snapshot: NotebookSnapshot
  /** The thread's user and assistant turns; Sofia composes the document from them. */
  messages: Array<{ role: "user" | "assistant"; content: string }>
}): Promise<Notebook> {
  const response = await fetchNotebooksProxy(
    args.organizationSlug,
    NOTEBOOKS_ENDPOINT,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organization: args.organizationSlug,
        workspace: args.workspaceId,
        title: args.title,
        mode: args.mode,
        repeat: args.repeat,
        snapshot: args.snapshot,
        messages: args.messages,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(await readError(response, "Could not save the notebook"))
  }
  const data = await parsePayload(
    response,
    z.object({ notebook: notebookSchema }),
  )
  return data.notebook
}

export async function deleteNotebook(
  organizationSlug: string,
  workspaceId: string,
  notebookId: string,
): Promise<void> {
  const response = await fetchNotebooksProxy(
    organizationSlug,
    `${NOTEBOOKS_ENDPOINT}/${encodeURIComponent(notebookId)}?organization=${encodeURIComponent(organizationSlug)}&workspace=${encodeURIComponent(workspaceId)}`,
    { method: "DELETE" },
  )
  if (!response.ok) {
    throw new Error(await readError(response, "Could not delete the notebook"))
  }
}

export async function renameNotebook(
  organizationSlug: string,
  workspaceId: string,
  notebookId: string,
  title: string,
): Promise<Notebook> {
  const response = await fetchNotebooksProxy(
    organizationSlug,
    `${NOTEBOOKS_ENDPOINT}/${encodeURIComponent(notebookId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organization: organizationSlug,
        workspace: workspaceId,
        title,
      }),
    },
  )
  if (!response.ok) {
    throw new Error(await readError(response, "Could not rename the notebook"))
  }
  const data = await parsePayload(
    response,
    z.object({ notebook: notebookSchema }),
  )
  return data.notebook
}

export async function updateNotebookDocument(
  organizationSlug: string,
  workspaceId: string,
  notebook: Notebook,
  document: NotebookDocument,
  locked?: boolean,
): Promise<Notebook> {
  const response = await fetchNotebooksProxy(
    organizationSlug,
    `${NOTEBOOKS_ENDPOINT}/${encodeURIComponent(notebook.id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organization: organizationSlug,
        workspace: workspaceId,
        document,
        ...(locked === undefined ? {} : { locked }),
        expected_updated_at: notebook.updated_at,
      }),
    },
  )
  if (!response.ok) {
    const message = await readError(response, "Could not save notebook")
    if (response.status === 409) {
      throw new NotebookConflictError(message)
    }
    throw new Error(message)
  }
  return (await parsePayload(response, z.object({ notebook: notebookSchema })))
    .notebook
}

export class NotebookConflictError extends Error {}

export async function chatWithNotebook(
  organizationSlug: string,
  workspaceId: string,
  notebook: Notebook,
  message: string,
): Promise<Notebook> {
  const response = await fetchNotebooksProxy(
    organizationSlug,
    `${NOTEBOOKS_ENDPOINT}/${encodeURIComponent(notebook.id)}/chat`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organization: organizationSlug,
        workspace: workspaceId,
        message,
        expected_updated_at: notebook.updated_at,
      }),
    },
  )
  if (!response.ok) {
    const message = await readError(
      response,
      "Sofia could not update the notebook",
    )
    if (response.status === 409) {
      throw new NotebookConflictError(message)
    }
    throw new Error(message)
  }
  return (await parsePayload(response, z.object({ notebook: notebookSchema })))
    .notebook
}
